import React, { useMemo, useState } from 'react'

import cn from 'classnames'
import Modal from 'react-modal'

import ChainToken from '@src/components/ChainToken'
import SearchIcon from '@src/components/icons/Search'
import { getChainNameFromChainId } from '@src/configs/chainInfo'
import { INetwork } from '@src/types/network'
import { IToken } from '@src/types/token'

/**
 * Props for the SelectTokenModal
 */
interface ISelectTokenModalProps {
    isOpen: boolean
    onClose: () => void
    /**
     * A record (dictionary) of chains. The key should be the chain ID (string)
     * and the value is an object with chain info (name, icon, etc.).
     */
    availableNetworks: Record<string, INetwork>

    /** Which chain is currently selected */
    selectedChain: string | number

    /** Callback to update the selected chain */
    setSelectedChain: (chainId: string | number) => void

    /** The currently selected token (if any) */
    selectedToken?: IToken

    /** Callback to update the selected token */
    setSelectedToken: (token: IToken) => void

    /** A list of chain IDs that are disabled/unavailable */
    disabledChains?: number[]

    /** Explicitly pass in a list of tokens you allow to be shown/selectable */
    allowedTokens: IToken[]
}

/**
 * A simple chain selector for either mobile/desktop columns.
 * Feel free to style as you wish or inline this.
 */
function ChainSelector({
    chains,
    selectedChain,
    disabledChains = [],
    onChainClicked,
}: {
    chains: Record<string, INetwork>
    selectedChain: string | number
    disabledChains?: number[]
    onChainClicked: (chainId: number) => void
}) {
    return (
        <>
            {Object.entries(chains).map(([chainIdStr, chain]) => {
                const chainIdNum = Number(chainIdStr)
                const isDisabled = disabledChains.includes(chainIdNum)
                const isSelected = Number(selectedChain) === chainIdNum
                return (
                    <button
                        key={chainIdStr}
                        className={cn(
                            'flex w-full items-center gap-3 rounded-md px-4 py-3.5 transition-colors duration-200 hover:bg-token-modal-chain-hover',
                            isSelected && 'border border-token-modal-border bg-token-modal-chain-selected',
                            isDisabled && 'cursor-not-allowed opacity-50'
                        )}
                        onClick={() => !isDisabled && onChainClicked(chainIdNum)}
                        disabled={isDisabled}
                    >
                        {chain.icon && <img src={chain.icon} alt={chain.name} className='h-5 w-5 rounded-sm' />}
                        <span className='truncate'>{chain.name}</span>
                    </button>
                )
            })}
        </>
    )
}

/**
 * A simple list of tokens (no categories, no balances).
 * If you want categories or more advanced grouping, feel free to re-implement it.
 */
function TokenResultsDisplay({
    tokens,
    onTokenClicked,
    selectedToken,
}: {
    tokens: IToken[]
    onTokenClicked: (token: IToken) => void
    selectedToken?: IToken
}) {
    if (tokens.length === 0) {
        return <p className='mt-4 text-center text-sm text-gray-400'>No tokens found.</p>
    }

    return (
        <div className='flex flex-col gap-2'>
            {tokens.map(token => {
                const isSelected = token.address === selectedToken?.address && token.chainId === selectedToken.chainId
                return (
                    <button
                        key={`${token.chainId}-${token.address}`}
                        onClick={() => onTokenClicked(token)}
                        className={cn(
                            'flex w-full items-center gap-3 rounded-md px-4 py-3.5 transition-colors duration-200 hover:bg-token-modal-chain-hover',
                            isSelected && 'border border-token-modal-border bg-token-modal-chain-selected'
                        )}
                    >
                        <ChainToken
                            tokenLogo={token.name === 'BitTorrent' ? '/assets/token/btt.png' : token.logoURI}
                            networkIcon={`/assets/network/${getChainNameFromChainId(token.chainId)}.png`}
                            tokenIconSize={28}
                            chainIconSize={14}
                        />
                        <div className='flex flex-1 flex-col items-start gap-0.5 overflow-hidden'>
                            <span className='text-left text-[14px] font-bold'>{token.symbol}</span>
                            <span className='overflow-hidden text-ellipsis text-left text-xs text-token-modal-label-text'>
                                {token.name}
                            </span>
                        </div>
                    </button>
                )
            })}
        </div>
    )
}

/**
 * The main token selection modal
 */
export default function SelectTokenModal({
    isOpen,
    onClose,
    availableNetworks,
    selectedChain,
    setSelectedChain,
    selectedToken,
    setSelectedToken,
    disabledChains = [],
    allowedTokens,
}: ISelectTokenModalProps): JSX.Element {
    const [tokenQuery, setTokenQuery] = useState('')

    // Modal styles (shared between mobile and desktop), just like before.
    const modalStyle = {
        overlay: {
            position: 'fixed' as const,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'var(--token-modal-overlay)', // if you have these CSS vars
            zIndex: 50,
            padding: '16px',
        },
        content: {
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            transform: 'translate(-50%, -50%)',
            width: '95%',
            maxWidth: '576px',
            height: '680px',
            padding: 0,
            border: '1px solid var(--token-modal-border)',
            borderRadius: '0.375rem',
            background: 'var(--token-modal-background)',
            overflow: 'hidden',
        },
    }

    /**
     * Filter the allowed tokens by:
     * 1) The selected chain
     * 2) The search query (by symbol, name, or address)
     */
    const filteredTokens = useMemo(() => {
        const chainIdNum = Number(selectedChain)
        return allowedTokens.filter(token => {
            // 1) Must match chain if chain is selected
            if (chainIdNum && token.chainId !== chainIdNum) {
                return false
            }

            // 2) Must match the query (symbol, name, or address)
            const q = tokenQuery.trim().toLowerCase()
            if (!q) return true // no query => show all in that chain
            return (
                token.symbol.toLowerCase().includes(q) ||
                token.name.toLowerCase().includes(q) ||
                token.address.toLowerCase() === q
            )
        })
    }, [allowedTokens, selectedChain, tokenQuery])

    const handleTokenSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setTokenQuery(e.target.value)
    }

    const handleTokenClick = (token: IToken) => {
        // Update chain (in case user typed an address from a different chain)
        setSelectedChain(token.chainId)
        // Update token
        setSelectedToken(token)
        onClose()
    }

    return (
        <Modal isOpen={isOpen} onRequestClose={onClose} style={modalStyle} ariaHideApp={false}>
            {/* MOBILE VIEW */}
            <div className='flex h-full flex-col sm:hidden'>
                {/* Mobile Header */}
                <div className='flex items-center justify-between p-4'>
                    <h2 className='text-[20px] font-semibold'>Select asset</h2>
                    <button
                        onClick={onClose}
                        className='text-white/70 transition-colors hover:text-white'
                        type='button'
                    >
                        <svg width='24' height='24' fill='none' viewBox='0 0 24 24'>
                            <path
                                d='M18 6L6 18M6 6L18 18'
                                stroke='currentColor'
                                strokeWidth='2'
                                strokeLinecap='round'
                            />
                        </svg>
                    </button>
                </div>

                {/* Mobile Search */}
                <div className='px-4 pb-4'>
                    <div className='flex w-full items-center gap-2 rounded-md border border-token-modal-border bg-token-modal-input-bg px-3 py-2.5'>
                        <SearchIcon className='h-5 w-5 text-token-modal-input-placeholder' />
                        <input
                            type='text'
                            placeholder='Search by symbol, name or address'
                            value={tokenQuery}
                            onChange={handleTokenSearchChange}
                            className='w-full bg-transparent text-white placeholder-token-modal-input-placeholder outline-none'
                        />
                    </div>
                </div>

                {/* Mobile Chains Section */}
                <div className='px-4 pb-3'>
                    <h3 className='mb-2 text-[20px] font-semibold'>Chains</h3>
                    <div className='grid grid-cols-4 gap-1.5'>
                        <ChainSelector
                            chains={availableNetworks}
                            selectedChain={selectedChain}
                            disabledChains={disabledChains}
                            onChainClicked={chainId => setSelectedChain(chainId)}
                        />
                    </div>
                </div>

                {/* Mobile Assets Section */}
                <div className='flex-1 overflow-y-auto px-4'>
                    <h3 className='mb-4 text-[20px] font-semibold'>Assets</h3>
                    <TokenResultsDisplay
                        tokens={filteredTokens}
                        onTokenClicked={handleTokenClick}
                        selectedToken={selectedToken}
                    />
                </div>
            </div>

            {/* DESKTOP VIEW */}
            <div className='hidden h-full flex-col sm:flex'>
                <div className='flex'>
                    <div className='w-[192px] border-r border-token-modal-divider-dark p-4'>
                        <h2 className='text-[20px] font-semibold'>Chains</h2>
                    </div>
                    <div className='flex flex-1 items-center justify-between p-4'>
                        <h2 className='text-[20px] font-semibold'>Assets</h2>
                        <button
                            onClick={onClose}
                            className='text-white/70 transition-colors hover:text-white'
                            type='button'
                        >
                            <svg width='24' height='24' fill='none' viewBox='0 0 24 24'>
                                <path
                                    d='M18 6L6 18M6 6L18 18'
                                    stroke='currentColor'
                                    strokeWidth='2'
                                    strokeLinecap='round'
                                />
                            </svg>
                        </button>
                    </div>
                </div>

                <div className='flex min-h-0 flex-1 flex-col sm:flex-row'>
                    {/* Left panel: Chains */}
                    <div className='w-[192px] flex-col border-r border-token-modal-divider-dark'>
                        <div className='no-scrollbar h-full overflow-y-auto p-3'>
                            <ChainSelector
                                chains={availableNetworks}
                                selectedChain={selectedChain}
                                disabledChains={disabledChains}
                                onChainClicked={chainId => setSelectedChain(chainId)}
                            />
                        </div>
                    </div>

                    {/* Right panel: Search + Tokens */}
                    <div className='flex min-h-0 w-full flex-1 flex-col'>
                        {/* Search Bar */}
                        <div className='px-3 pt-3'>
                            <div className='flex w-full items-center gap-2 rounded-md border border-token-modal-border bg-token-modal-input-bg px-3 py-2.5'>
                                <SearchIcon className='h-5 w-5 text-token-modal-input-placeholder' />
                                <input
                                    type='text'
                                    placeholder='Search by symbol, name or address'
                                    value={tokenQuery}
                                    onChange={handleTokenSearchChange}
                                    className='w-full bg-transparent text-white placeholder-token-modal-input-placeholder outline-none'
                                />
                            </div>
                        </div>

                        <div className='h-full overflow-y-auto p-3'>
                            <TokenResultsDisplay
                                tokens={filteredTokens}
                                onTokenClicked={handleTokenClick}
                                selectedToken={selectedToken}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    )
}
